import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { apiUrl } from '../../lib/api'
import { fetchMe, isAdminOrgType, shellForOrgType } from '../../lib/auth'
import { crossOriginRedirect, IS_ADMIN_ORIGIN } from '../../lib/origin-shell'
import { useAuthStore } from '../../store/auth'
import { useT } from '../../i18n'
import { AuthShell } from './AuthShell'

const COPY = {
  en: {
    title: 'Welcome back',
    description: 'Sign in to continue to your PEXIFY workspace.',
    email: 'Email address',
    password: 'Password',
    keep: 'Keep me signed in',
    forgot: 'Forgot password?',
    submit: 'Sign in',
    submitting: 'Signing in…',
    show: 'Show password',
    hide: 'Hide password',
    invalid: 'Sign in failed — check your email and password.',
    wrongAdmin: 'This account cannot access the Admin workspace. Sign in at app.pexify.net.',
    wrongApp: 'This account belongs to the Admin workspace. Sign in at admin.pexify.net.',
    unresolved: 'We could not find an active organization for this account.',
  },
  tr: {
    title: 'Tekrar hoş geldin',
    description: 'PEXIFY çalışma alanına devam etmek için giriş yap.',
    email: 'E-posta adresi',
    password: 'Parola',
    keep: 'Oturumu açık tut',
    forgot: 'Parolanı mı unuttun?',
    submit: 'Giriş yap',
    submitting: 'Giriş yapılıyor…',
    show: 'Parolayı göster',
    hide: 'Parolayı gizle',
    invalid: 'Giriş başarısız — e-posta ve parolanı kontrol et.',
    wrongAdmin: 'Bu hesap Admin çalışma alanına erişemez. app.pexify.net üzerinden giriş yap.',
    wrongApp: 'Bu hesap Admin çalışma alanına ait. admin.pexify.net üzerinden giriş yap.',
    unresolved: 'Bu hesaba bağlı aktif bir organizasyon bulunamadı.',
  },
} as const

export function LoginPage() {
  const t = useT<Record<string, string>>(COPY)
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const clearTokens = useAuthStore((s) => s.clearTokens)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [keepSignedIn, setKeepSignedIn] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const body = new URLSearchParams({ username: email, password })
      const res = await fetch(apiUrl('/auth/jwt/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      })
      if (!res.ok) {
        const problem = (await res.json().catch(() => ({}))) as {
          detail?: { message?: string } | string
        }
        const detail = problem.detail
        throw new Error(
          typeof detail === 'object' && detail?.message
            ? detail.message
            : typeof detail === 'string'
              ? detail
              : t.invalid,
        )
      }
      const data = (await res.json()) as { access_token: string; refresh_token: string }
      setTokens(data.access_token, data.refresh_token, keepSignedIn)
      const me = await fetchMe()
      if (IS_ADMIN_ORIGIN && !isAdminOrgType(me?.active_membership?.org_type)) {
        clearTokens()
        setError(t.wrongAdmin)
        return
      }
      const shell = shellForOrgType(me?.active_membership?.org_type)
      if (!shell) {
        clearTokens()
        setError(t.unresolved)
        return
      }
      if (crossOriginRedirect(shell)) {
        clearTokens()
        setError(t.wrongApp)
        return
      }
      navigate({ to: shell })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.invalid)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title={t.title} description={t.description}>
      <form onSubmit={handleSubmit} className="pex-auth-form">
        <div className="pex-auth-field">
          <label htmlFor="email">{t.email}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            className="pex-auth-input"
          />
        </div>
        <div className="pex-auth-field">
          <label htmlFor="password">{t.password}</label>
          <div className="pex-auth-input-wrap">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              className="pex-auth-input"
            />
            <button
              type="button"
              className="pex-auth-password-toggle"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? t.hide : t.show}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="pex-auth-row">
          <label className="pex-auth-checkbox">
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.target.checked)}
            />
            {t.keep}
          </label>
          <Link to="/forgot-password">{t.forgot}</Link>
        </div>
        {error && <div className="pex-auth-feedback" role="alert">{error}</div>}
        <button type="submit" disabled={loading} className="pex-auth-submit">
          {loading ? t.submitting : t.submit}
        </button>
      </form>
    </AuthShell>
  )
}
